import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  CreditCard,
  UserPlus,
  Settings,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  setOnboardingStep,
  updateOnboardingFormData,
  resetOnboarding,
  createTenant,
  validateSlug,
  selectOnboardingStep,
  selectOnboardingFormData,
  selectIsSlugAvailable,
  selectIsValidatingSlug,
  selectSlugSuggestion,
  selectIsCreatingTenant,
  selectCreatedTenantId,
  selectOnboardingError,
  OnboardingFormData,
} from '../../features/tenants/tenantSlice';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Stepper, StepContent, StepActions, Step } from '../ui/stepper';
import { PlanSelector, PlanType } from './PlanSelector';
import { onboardBilling } from '../../services/billingService';

const steps: Step[] = [
  { id: 'organization', title: 'Organization', description: 'Basic details' },
  { id: 'plan', title: 'Plan', description: 'Select plan' },
  { id: 'admin', title: 'Admin User', description: 'Initial admin' },
  { id: 'settings', title: 'Settings', description: 'Configuration' },
  { id: 'review', title: 'Review', description: 'Confirm' },
];

interface LabelProps {
  htmlFor: string;
  children: React.ReactNode;
  required?: boolean;
}

const Label: React.FC<LabelProps> = ({ htmlFor, children, required }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium mb-1.5">
    {children}
    {required && <span className="text-destructive ml-1">*</span>}
  </label>
);

interface FormFieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  required,
  error,
  hint,
  children,
}) => (
  <div className="space-y-1">
    <Label htmlFor={htmlFor} required={required}>
      {label}
    </Label>
    {children}
    {hint && !error && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    {error && <p className="text-xs text-destructive mt-1">{error}</p>}
  </div>
);

export const OnboardingWizard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentStep = useAppSelector(selectOnboardingStep);
  const formData = useAppSelector(selectOnboardingFormData);
  const isSlugAvailable = useAppSelector(selectIsSlugAvailable);
  const isValidatingSlug = useAppSelector(selectIsValidatingSlug);
  const slugSuggestion = useAppSelector(selectSlugSuggestion);
  const isCreating = useAppSelector(selectIsCreatingTenant);
  const createdTenantId = useAppSelector(selectCreatedTenantId);
  const error = useAppSelector(selectOnboardingError);

  // Validate slug when it changes
  useEffect(() => {
    if (formData.slug && formData.slug.length >= 3) {
      const timer = setTimeout(() => {
        dispatch(validateSlug(formData.slug));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.slug, dispatch]);

  // After the tenant is created, provision its billing (subscription + account)
  // then navigate to its details. Billing is best-effort — the tenant exists
  // regardless and pricing can be set later from the Billing page.
  useEffect(() => {
    if (!createdTenantId) return;
    let cancelled = false;
    (async () => {
      try {
        await onboardBilling(createdTenantId, {
          amount: Number(formData.billingAmount) || 0,
          billingCycle: formData.billingCycle,
          currency: formData.billingCurrency,
          billingEmail: formData.billingEmail || formData.adminEmail || undefined,
          tenantName: formData.organizationName,
          planTier: formData.plan,
        });
      } catch {
        /* tenant created; billing can be configured later from the Billing page */
      }
      if (cancelled) return;
      setTimeout(() => {
        navigate(`/tenants/${createdTenantId}`);
        dispatch(resetOnboarding());
      }, 1500);
    })();
    return () => {
      cancelled = true;
    };
  }, [createdTenantId, navigate, dispatch, formData]);

  const handleFieldChange = useCallback(
    (field: keyof OnboardingFormData, value: string) => {
      dispatch(updateOnboardingFormData({ [field]: value }));
    },
    [dispatch]
  );

  const handlePlanChange = useCallback(
    (plan: PlanType) => {
      dispatch(updateOnboardingFormData({ plan }));
    },
    [dispatch]
  );

  const generateSlug = useCallback((name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }, []);

  const handleOrganizationNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value;
      handleFieldChange('organizationName', name);
      // Auto-generate slug if slug is empty or matches previous auto-generated value
      if (!formData.slug || formData.slug === generateSlug(formData.organizationName)) {
        handleFieldChange('slug', generateSlug(name));
      }
    },
    [formData.slug, formData.organizationName, handleFieldChange, generateSlug]
  );

  const handleUseSuggestion = useCallback(() => {
    if (slugSuggestion) {
      handleFieldChange('slug', slugSuggestion);
    }
  }, [slugSuggestion, handleFieldChange]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      dispatch(setOnboardingStep(currentStep + 1));
    }
  }, [currentStep, dispatch]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      dispatch(setOnboardingStep(currentStep - 1));
    }
  }, [currentStep, dispatch]);

  const handleSubmit = useCallback(() => {
    dispatch(
      createTenant({
        tenant: {
          name: formData.organizationName,
          slug: formData.slug,
          domain: formData.domain || undefined,
          plan: formData.plan,
          settings: {
            timezone: formData.timezone,
            language: formData.language,
            currency: formData.currency,
          },
        },
        adminUser: {
          name: formData.adminName,
          email: formData.adminEmail,
          password: formData.adminPassword,
        },
      })
    );
  }, [dispatch, formData]);

  const handleCancel = useCallback(() => {
    dispatch(resetOnboarding());
    navigate('/tenants');
  }, [dispatch, navigate]);

  // Step validation
  const stepValidation = useMemo(() => {
    return {
      0: formData.organizationName.length >= 2 && formData.slug.length >= 3 && isSlugAvailable === true,
      1: !!formData.plan && !!formData.billingCurrency && !!formData.billingCycle &&
         (formData.billingAmount === '' || Number(formData.billingAmount) >= 0),
      2: formData.adminName.length >= 2 &&
         /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail) &&
         formData.adminPassword.length >= 8,
      3: !!formData.timezone && !!formData.language && !!formData.currency,
      4: true,
    };
  }, [formData, isSlugAvailable]);

  const canProceed = stepValidation[currentStep as keyof typeof stepValidation];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Organization Details
              </CardTitle>
              <CardDescription>
                Enter the basic information about the new tenant organization.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                label="Organization Name"
                htmlFor="organizationName"
                required
                hint="The display name for this organization"
              >
                <Input
                  id="organizationName"
                  value={formData.organizationName}
                  onChange={handleOrganizationNameChange}
                  placeholder="Acme Corporation"
                />
              </FormField>

              <FormField
                label="Slug"
                htmlFor="slug"
                required
                hint={
                  isValidatingSlug ? (
                    <span className="flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Checking availability...
                    </span>
                  ) : isSlugAvailable === true ? (
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <CheckCircle className="h-3 w-3" />
                      This slug is available
                    </span>
                  ) : isSlugAvailable === false ? (
                    <span className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3 text-destructive" />
                      <span className="text-destructive">This slug is taken.</span>
                      {slugSuggestion && (
                        <button
                          type="button"
                          onClick={handleUseSuggestion}
                          className="text-primary hover:underline"
                        >
                          Use {slugSuggestion}?
                        </button>
                      )}
                    </span>
                  ) : (
                    'URL-friendly identifier (e.g., acme-corp). Minimum 3 characters.'
                  )
                }
              >
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleFieldChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="acme-corp"
                />
              </FormField>

              <FormField
                label="Custom Domain"
                htmlFor="domain"
                hint="Optional custom domain for this tenant"
              >
                <Input
                  id="domain"
                  value={formData.domain}
                  onChange={(e) => handleFieldChange('domain', e.target.value)}
                  placeholder="admin.acme.com"
                />
              </FormField>
            </CardContent>
          </Card>
        );

      case 1:
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Plan & Billing
              </CardTitle>
              <CardDescription>
                Choose the tier, then set the subscription price KaziPeople will bill this organization.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <PlanSelector selectedPlan={formData.plan} onSelectPlan={handlePlanChange} />

              <div className="border-t border-border pt-5">
                <h4 className="font-medium mb-1">Subscription</h4>
                <p className="text-xs text-muted-foreground mb-4">
                  Pricing is platform-set. Leave the amount at 0 for a free/unbilled tenant (no subscription is created).
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField label="Currency" htmlFor="billingCurrency" required>
                    <select
                      id="billingCurrency"
                      value={formData.billingCurrency}
                      onChange={(e) => handleFieldChange('billingCurrency', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="TZS">TZS - Tanzanian Shilling</option>
                      <option value="KES">KES - Kenyan Shilling</option>
                      <option value="UGX">UGX - Ugandan Shilling</option>
                      <option value="RWF">RWF - Rwandan Franc</option>
                      <option value="USD">USD - US Dollar</option>
                    </select>
                  </FormField>

                  <FormField label="Amount" htmlFor="billingAmount" required hint="Per billing period">
                    <Input
                      id="billingAmount"
                      type="number"
                      min={0}
                      value={formData.billingAmount}
                      onChange={(e) => handleFieldChange('billingAmount', e.target.value)}
                      placeholder="1000000"
                    />
                  </FormField>

                  <FormField label="Billing cycle" htmlFor="billingCycle" required>
                    <select
                      id="billingCycle"
                      value={formData.billingCycle}
                      onChange={(e) => handleFieldChange('billingCycle', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </FormField>
                </div>

                <div className="mt-4">
                  <FormField label="Billing email" htmlFor="billingEmail" hint="Where invoices & receipts are sent (defaults to the admin email)">
                    <Input
                      id="billingEmail"
                      type="email"
                      value={formData.billingEmail}
                      onChange={(e) => handleFieldChange('billingEmail', e.target.value)}
                      placeholder="billing@acme.com"
                    />
                  </FormField>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Initial Admin User
              </CardTitle>
              <CardDescription>
                Create the first administrator account for this tenant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Full Name" htmlFor="adminName" required>
                <Input
                  id="adminName"
                  value={formData.adminName}
                  onChange={(e) => handleFieldChange('adminName', e.target.value)}
                  placeholder="John Doe"
                />
              </FormField>

              <FormField
                label="Email Address"
                htmlFor="adminEmail"
                required
                hint="This will be used for login"
              >
                <Input
                  id="adminEmail"
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => handleFieldChange('adminEmail', e.target.value)}
                  placeholder="admin@acme.com"
                />
              </FormField>

              <FormField
                label="Password"
                htmlFor="adminPassword"
                required
                hint="Minimum 8 characters"
              >
                <Input
                  id="adminPassword"
                  type="password"
                  value={formData.adminPassword}
                  onChange={(e) => handleFieldChange('adminPassword', e.target.value)}
                  placeholder="Enter a secure password"
                />
              </FormField>
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configuration
              </CardTitle>
              <CardDescription>
                Set up the default configuration for this tenant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Timezone" htmlFor="timezone" required>
                <select
                  id="timezone"
                  value={formData.timezone}
                  onChange={(e) => handleFieldChange('timezone', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">Eastern Time (US)</option>
                  <option value="America/Chicago">Central Time (US)</option>
                  <option value="America/Denver">Mountain Time (US)</option>
                  <option value="America/Los_Angeles">Pacific Time (US)</option>
                  <option value="Europe/London">London</option>
                  <option value="Europe/Paris">Paris</option>
                  <option value="Asia/Tokyo">Tokyo</option>
                  <option value="Asia/Shanghai">Shanghai</option>
                </select>
              </FormField>

              <FormField label="Language" htmlFor="language" required>
                <select
                  id="language"
                  value={formData.language}
                  onChange={(e) => handleFieldChange('language', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="ja">Japanese</option>
                  <option value="zh">Chinese</option>
                </select>
              </FormField>

              <FormField label="Currency" htmlFor="currency" required>
                <select
                  id="currency"
                  value={formData.currency}
                  onChange={(e) => handleFieldChange('currency', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                  <option value="CNY">CNY - Chinese Yuan</option>
                </select>
              </FormField>
            </CardContent>
          </Card>
        );

      case 4:
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Review & Confirm
              </CardTitle>
              <CardDescription>
                Please review all the information before creating the tenant.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {createdTenantId ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Tenant Created Successfully!</h3>
                  <p className="text-muted-foreground">
                    Redirecting to tenant details...
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {error && (
                    <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                      <p className="text-destructive flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        {error}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Organization
                      </h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Name:</dt>
                          <dd className="font-medium">{formData.organizationName}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Slug:</dt>
                          <dd className="font-medium">{formData.slug}</dd>
                        </div>
                        {formData.domain && (
                          <div className="flex justify-between">
                            <dt className="text-muted-foreground">Domain:</dt>
                            <dd className="font-medium">{formData.domain}</dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Plan & Billing
                      </h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Tier:</dt>
                          <dd className="font-medium capitalize">{formData.plan}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Subscription:</dt>
                          <dd className="font-medium">
                            {Number(formData.billingAmount) > 0
                              ? `${formData.billingCurrency} ${Number(formData.billingAmount).toLocaleString()} / ${formData.billingCycle === 'yearly' ? 'yr' : 'mo'}`
                              : 'Free (no subscription)'}
                          </dd>
                        </div>
                        {formData.billingEmail && (
                          <div className="flex justify-between">
                            <dt className="text-muted-foreground">Billing email:</dt>
                            <dd className="font-medium">{formData.billingEmail}</dd>
                          </div>
                        )}
                      </dl>
                    </div>

                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <UserPlus className="h-4 w-4" />
                        Admin User
                      </h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Name:</dt>
                          <dd className="font-medium">{formData.adminName}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Email:</dt>
                          <dd className="font-medium">{formData.adminEmail}</dd>
                        </div>
                      </dl>
                    </div>

                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        Settings
                      </h4>
                      <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Timezone:</dt>
                          <dd className="font-medium">{formData.timezone}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Language:</dt>
                          <dd className="font-medium">{formData.language}</dd>
                        </div>
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground">Currency:</dt>
                          <dd className="font-medium">{formData.currency}</dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Stepper steps={steps} currentStep={currentStep} />

      <StepContent>{renderStepContent()}</StepContent>

      {!createdTenantId && (
        <StepActions>
          <div>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="outline" onClick={handleBack}>
                Back
              </Button>
            )}
            {currentStep < steps.length - 1 ? (
              <Button onClick={handleNext} disabled={!canProceed}>
                Next
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Tenant'
                )}
              </Button>
            )}
          </div>
        </StepActions>
      )}
    </div>
  );
};

export default OnboardingWizard;
